import type { ReactNode } from "react";
import { PhoneNav } from "../../../shared/phone/PhoneNav";
import { PnRow, SaveBar, Segmented } from "../../../shared/phone/Row";
import type { ProgramEmailPrefs, ProgramRewardDuration, ProgramRewardType, ProgramRewardUnit } from "../../../shared/types";
import { useProgramSettings } from "../../components/ProgramSettingsModal";
import { Icon, type IconName } from "../../components/ui/Icon";
import { FieldRow, plural } from "./parts";

type Part = "reward" | "rules" | "assets" | "documents" | "emails";

const PARTS: [Part, string, IconName][] = [
  ["reward", "Reward", "coin"],
  ["rules", "Rules", "list"],
  ["assets", "Assets", "photo"],
  ["documents", "Documents", "doc"],
  ["emails", "Emails", "mail"],
];

const CURRENCIES: [string, string][] = [
  ["USD", "US Dollar"],
  ["EUR", "Euro"],
  ["GBP", "British Pound"],
  ["CAD", "Canadian Dollar"],
  ["AUD", "Australian Dollar"],
];

const DURATION_TAIL: Record<ProgramRewardDuration, string> = {
  first: "first payment only",
  recurring: "recurring",
  forever: "for as long as they pay",
};

const EMAILS: [keyof ProgramEmailPrefs, string, string][] = [
  ["newSale", "New sale", "When a referral becomes a customer"],
  ["rewardReady", "Reward ready", "When a reward clears the hold"],
  ["payoutSent", "Payout sent", "When you record them as paid"],
  ["inactivityNudge", "Inactivity nudge", "If no clicks for 30 days"],
];

type Settings = ReturnType<typeof useProgramSettings>;

/** An iOS switch in a row: the setting's name and what it means, the switch on the right. */
function SwitchRow({ title, sub, on, onToggle, icon }: { title: string; sub?: string; on: boolean; onToggle: () => void; icon?: ReactNode }) {
  return (
    <div className="pn-row static wrap pa-swrow">
      {icon}
      <span className="m">
        <span className="t">{title}</span>
        {sub ? <span className="s">{sub}</span> : null}
      </span>
      <button type="button" className="pn-sw" role="switch" aria-checked={on} aria-label={title} onClick={onToggle} />
    </div>
  );
}

/** One line that says what each part is set to, under its row on the Program screen. */
function summary(k: Part, ps: Settings): string {
  if (k === "reward") {
    if (ps.rewardType === "ladder") return `Rises with volume, from ${ps.tiers[0]?.value ?? ps.rewardValue}${ps.rateSuffix === "%" ? "%" : ""}`;
    const rate = ps.rewardUnit === "percent" ? `${ps.rewardValue}%` : `${ps.sym}${ps.rewardValue}`;
    return `${rate} of each payment, ${DURATION_TAIL[ps.duration]}`;
  }
  if (k === "rules") return `${ps.cookieDays}-day window, ${ps.sym}${ps.minPayout} minimum`;
  if (k === "assets") {
    if (!ps.folders) return "Folders shared with partners";
    const n = ps.folders.filter((f) => f.sharedWithPartners).length;
    return n ? `${plural(n, "folder")} shared with partners` : "Nothing shared yet";
  }
  if (k === "documents") return "Agreement and tax form required";
  return `${EMAILS.filter(([key]) => ps.emailPrefs[key]).length} of ${EMAILS.length} turned on`;
}

/**
 * Program settings on a phone or an iPad: five rows, each opening its own screen with switches and 16px
 * fields, and a save bar once anything changed. The screen lives in the Partners address (`?program=part`).
 */
export function ProgramScreens({
  productId,
  productName,
  part,
  say,
}: {
  productId: string;
  productName: string | null;
  part: string;
  /** The Partners screen's note after an action. */
  say: (msg: string, ok?: boolean) => void;
}) {
  const ps = useProgramSettings(productId);
  const partners = `/products/${encodeURIComponent(productId)}/partners`;
  const here = PARTS.find(([k]) => k === part);

  const saveBar = (
    <>
      {ps.saveErr ? (
        <p className="pa-err" role="alert">
          {ps.saveErr}
        </p>
      ) : null}
      <SaveBar
        dirty={ps.dirty}
        saving={ps.saving}
        onDiscard={ps.reset}
        onSave={async () => {
          if (await ps.save()) say("Program saved");
        }}
      />
    </>
  );

  const state = ps.loading ? (
    <div className="pn-empty">Loading the program…</div>
  ) : ps.loadErr ? (
    <div className="pn-empty">
      <b>Couldn't load the program</b>
      {ps.loadErr}
    </div>
  ) : null;

  if (!here) {
    return (
      <div className="pn-screen">
        <PhoneNav title="Program" large sub={productName ? `${productName} partners` : undefined} back={{ to: partners, label: "Partners" }} />
        {state ?? (
          <div className="pn-stack">
            <div className="pn-grp">
              {PARTS.map(([k, t, i]) => (
                <PnRow key={k} to={`${partners}?program=${k}`} icon={<Icon name={i} />} iconTone="violet" title={t} sub={summary(k, ps)} />
              ))}
            </div>
          </div>
        )}
        {saveBar}
      </div>
    );
  }

  const [k, title] = here;
  return (
    <div className="pn-screen">
      <PhoneNav title={title} large back={{ to: `${partners}?program`, label: "Program" }} />
      {state ?? (
        <div className="pn-stack tight">
          {k === "reward" ? <RewardPart ps={ps} /> : null}
          {k === "rules" ? <RulesPart ps={ps} /> : null}
          {k === "assets" ? <AssetsPart ps={ps} productId={productId} say={say} /> : null}
          {k === "documents" ? <DocumentsPart ps={ps} productId={productId} /> : null}
          {k === "emails" ? <EmailsPart ps={ps} /> : null}
        </div>
      )}
      {saveBar}
    </div>
  );
}

// --- the five parts ---

function RewardPart({ ps }: { ps: Settings }) {
  const pct = ps.rewardUnit === "percent";
  return (
    <>
      <section>
        <div className="pn-cap">How the rate works</div>
        <Segmented<ProgramRewardType>
          label="How the rate works"
          value={ps.rewardType}
          onChange={ps.switchRewardType}
          options={[
            { value: "flat", label: "Flat rate" },
            { value: "ladder", label: "Rises with volume" },
          ]}
        />
      </section>
      {ps.rewardType === "flat" ? (
        <section>
          <div className="pn-cap">Rate every partner earns</div>
          <Segmented<ProgramRewardUnit>
            label="Reward unit"
            value={ps.rewardUnit}
            onChange={ps.setRewardUnit}
            options={[
              { value: "percent", label: "Percent" },
              { value: "fixed", label: `Flat ${ps.sym}` },
            ]}
          />
          <div className="pn-grp pa-gap">
            <FieldRow
              id="pa-pp-rate"
              label={pct ? "Rate" : "Per sale"}
              value={ps.rewardValue}
              onChange={ps.setRewardValue}
              suf={pct ? "%" : undefined}
              pre={pct ? undefined : ps.sym}
            />
          </div>
        </section>
      ) : (
        <section>
          <div className="pn-cap">Rate by sales so far</div>
          <div className="pn-grp">
            {ps.tiers.map((t, i) => {
              const top = t.upToCount === null;
              const prev = i > 0 ? ps.tiers[i - 1].upToCount : null;
              return (
                <div className="pn-irow pa-tier" key={t.key}>
                  {top ? (
                    <span className="pa-tl">Over {prev ?? "the last tier"} sales</span>
                  ) : (
                    <span className="pa-tl">
                      Up to
                      <input
                        className="pa-tc"
                        inputMode="numeric"
                        value={String(t.upToCount ?? "")}
                        onChange={(e) => ps.setTierCount(t.key, e.target.value)}
                        aria-label={`Tier ${i + 1} sales ceiling`}
                      />
                      sales
                    </span>
                  )}
                  <span className="pa-val">
                    <input
                      inputMode="decimal"
                      value={t.value}
                      style={{ width: `calc(${Math.max(t.value.length, 1)}ch + 2px)` }}
                      onChange={(e) => ps.setTierValue(t.key, e.target.value)}
                      aria-label={`Tier ${i + 1} rate`}
                    />
                    <span className="pa-suf">{ps.rateSuffix}</span>
                  </span>
                  <button type="button" className="pa-x" onClick={() => ps.removeTier(t.key)} aria-label={`Remove tier ${i + 1}`}>
                    <Icon name="x" />
                  </button>
                </div>
              );
            })}
            <button type="button" className="pn-row accent" onClick={ps.addTier}>
              <span className="pn-ico">
                <Icon name="plus" />
              </span>
              <span className="m">
                <span className="t">Add a tier</span>
              </span>
            </button>
          </div>
        </section>
      )}
      <section>
        <div className="pn-cap">Pay for how long</div>
        <Segmented<ProgramRewardDuration>
          label="Pay for how long"
          value={ps.duration}
          onChange={ps.setDuration}
          options={[
            { value: "first", label: "First payment" },
            { value: "recurring", label: "Recurring" },
            { value: "forever", label: "Forever" },
          ]}
        />
      </section>
      <section>
        <div className="pn-grp">
          <FieldRow id="pa-pp-bonus" label="First-sale bonus" value={ps.bonus} onChange={ps.setBonus} pre={ps.sym} />
        </div>
        <p className="pn-note">Paid once, on top of the rate, on a partner's first sale.</p>
      </section>
    </>
  );
}

function RulesPart({ ps }: { ps: Settings }) {
  return (
    <>
      <section>
        <div className="pn-grp">
          <div className="pn-irow col">
            <label htmlFor="pa-pp-link">Partner link sends to</label>
            <input
              id="pa-pp-link"
              type="url"
              inputMode="url"
              autoCapitalize="off"
              autoCorrect="off"
              value={ps.linkBaseInput}
              placeholder="yoursite.com/go"
              onChange={(e) => ps.setLinkBaseInput(e.target.value)}
            />
          </div>
        </div>
        <p className="pn-note">Each partner's code goes on the end of this link.</p>
      </section>
      <section>
        <div className="pn-grp">
          <FieldRow id="pa-pp-cookie" label="A click counts for" value={ps.cookieDays} onChange={ps.setCookieDays} suf="days" inputMode="numeric" />
          <FieldRow id="pa-pp-hold" label="Hold before a reward pays" value={ps.holdDays} onChange={ps.setHoldDays} suf="days" inputMode="numeric" />
          <FieldRow id="pa-pp-min" label="Minimum payout" value={ps.minPayout} onChange={ps.setMinPayout} pre={ps.sym} />
          <div className="pn-irow pa-f">
            <label htmlFor="pa-pp-cur">Currency</label>
            <select id="pa-pp-cur" value={ps.currency} onChange={(e) => ps.setCurrency(e.target.value)}>
              {CURRENCIES.map(([code, name]) => (
                <option key={code} value={code}>
                  {code} · {name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </section>
      <section>
        <div className="pn-grp">
          <SwitchRow
            title="Give each partner a coupon code"
            sub="A code each partner shares, like SARAH15. Set the matching discount up in your own checkout."
            on={ps.couponEnabled}
            onToggle={() => ps.setCouponEnabled(!ps.couponEnabled)}
          />
        </div>
      </section>
    </>
  );
}

function AssetsPart({ ps, productId, say }: { ps: Settings; productId: string; say: (msg: string, ok?: boolean) => void }) {
  return (
    <>
      <section>
        <div className="pn-cap">Shared with partners</div>
        <div className="pn-grp">
          {ps.folders === null ? (
            <div className="pn-row static">
              <span className="m">
                <span className="s">Loading the folders…</span>
              </span>
            </div>
          ) : ps.folders.length === 0 ? (
            <div className="pn-row static">
              <span className="m">
                <span className="s">No folders yet.</span>
              </span>
            </div>
          ) : (
            ps.folders.map((f) => (
              <SwitchRow
                key={f.id}
                icon={
                  <span className={f.sharedWithPartners ? "pn-ico" : "pn-ico plain"}>
                    <Icon name="folder" />
                  </span>
                }
                title={f.name}
                sub={plural(f.itemCount, "item")}
                on={f.sharedWithPartners}
                onToggle={async () => {
                  const to = !f.sharedWithPartners;
                  const ok = await ps.shareFolder(f, to);
                  say(ok ? (to ? `${f.name} is shared with partners` : `${f.name} is no longer shared`) : `Couldn't change ${f.name}. Try again.`, ok);
                }}
              />
            ))
          )}
          {ps.rootShared > 0 ? (
            <PnRow icon={<Icon name="doc" />} iconTone="plain" title="Single files" sub={`${plural(ps.rootShared, "file")} shared on their own`} />
          ) : null}
        </div>
        <p className="pn-note">A shared folder shows in every partner's portal.</p>
      </section>
      <section>
        <div className="pn-grp">
          <PnRow
            to={`/products/${encodeURIComponent(productId)}?assets`}
            icon={<Icon name="photo" />}
            title="All assets"
            sub="Upload, write swipe copy and share single files"
            wrap
          />
        </div>
      </section>
    </>
  );
}

function DocumentsPart({ ps, productId }: { ps: Settings; productId: string }) {
  return (
    <>
      <section>
        <div className="pn-grp">
          <PnRow
            icon={<Icon name="doc" />}
            title="Partner agreement"
            sub={ps.agreementVersion !== null ? `Version ${ps.agreementVersion}, signed in the portal` : "Signed in the portal"}
            value="Required"
          />
          <PnRow icon={<Icon name="doc" />} title="Tax form (W-9 or W-8)" sub="Collected in the portal" value="Required" />
        </div>
        <p className="pn-note">Both must be complete before a partner can be paid.</p>
      </section>
      <section>
        <div className="pn-grp">
          <PnRow
            to={`/products/${encodeURIComponent(productId)}/agreement`}
            icon={<Icon name="pen" />}
            iconTone="violet"
            title="Edit the agreement"
            sub="Clauses, and where partners sign"
          />
        </div>
      </section>
    </>
  );
}

function EmailsPart({ ps }: { ps: Settings }) {
  return (
    <section>
      <div className="pn-grp">
        {EMAILS.map(([key, title, sub]) => (
          <SwitchRow key={key} title={title} sub={sub} on={ps.emailPrefs[key]} onToggle={() => ps.setEmail(key)} />
        ))}
      </div>
      <p className="pn-note">Partners get these once sending turns on in a later update.</p>
    </section>
  );
}
